import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { SampleStatusBadges } from '../../components/common/SampleStatusBadges';
import { LABEL_SOURCE_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, EvalGtSample, LABEL_SOURCE_LABELS } from '../../types';
import { fmtIso } from '../../utils';

export const GtSamplesListPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [samples, setSamples] = useState<EvalGtSample[]>([]);
  const [total, setTotal] = useState(0);
  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);
  const [loading, setLoading] = useState(false);

  const [filterDatasetId, setFilterDatasetId] = useState('');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterIngestedBy, setFilterIngestedBy] = useState('');

  useEffect(() => {
    EvalApiService.listDatasets(token ?? '').then(({ items }) => setDatasets(items));
  }, [token]);

  useEffect(() => {
    setLoading(true);
    const chainId = filterChainId ? Number(filterChainId) : undefined;
    EvalApiService.listGtSamples(token ?? '', {
      dataset_id:
        filterDatasetId && filterDatasetId !== '__unassigned__' ? filterDatasetId : undefined,
      unassigned: filterDatasetId === '__unassigned__' ? true : undefined,
      chain_id: chainId,
      ingested_by: filterIngestedBy || undefined,
    })
      .then(({ items, total: t }) => {
        setSamples(items);
        setTotal(t);
      })
      .finally(() => setLoading(false));
  }, [token, filterDatasetId, filterChainId, filterIngestedBy]);

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">GT Samples</div>
          <div className="eval-page-sub">{total} total samples</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <select
          className="eval-filter-select"
          value={filterDatasetId}
          onChange={(e) => setFilterDatasetId(e.target.value)}
        >
          <option value="">All datasets</option>
          <option value="__unassigned__">— unassigned —</option>
          {datasets.map((d) => (
            <option key={d.dataset_id} value={d.dataset_id}>
              {d.dataset_name}
            </option>
          ))}
        </select>
        <input
          className="eval-form-input"
          placeholder="chain_id"
          value={filterChainId}
          onChange={(e) => setFilterChainId(e.target.value.replace(/\D/g, ''))}
          style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <input
          className="eval-form-input"
          placeholder="ingested by"
          value={filterIngestedBy}
          onChange={(e) => setFilterIngestedBy(e.target.value)}
          style={{ width: 180, fontSize: 12 }}
        />
        {(filterDatasetId || filterChainId || filterIngestedBy) && (
          <button
            className="eval-btn-secondary"
            onClick={() => {
              setFilterDatasetId('');
              setFilterChainId('');
              setFilterIngestedBy('');
            }}
          >
            Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          {loading ? 'Loading…' : `${samples.length} shown`}
        </div>
      </div>

      <div
        className="eval-inference-grid"
        style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s' }}
      >
        {samples.map((inf) => (
          <div
            key={inf.gt_sample_id}
            className="eval-inference-thumb"
            onClick={() => navigate(`/eval/gt-samples/${inf.gt_sample_id}`)}
          >
            <div className="eval-inference-thumb-img">
              {inf.image_url ? (
                <img
                  src={inf.image_url}
                  alt={`image_id: ${inf.image_id}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : inf.image_id != null ? (
                `image_id: ${inf.image_id}`
              ) : (
                inf.gt_sample_id
              )}
            </div>
            <div className="eval-inference-thumb-meta">
              <div
                style={{
                  marginBottom: 2,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                }}
              >
                {inf.gt_sample_id}
              </div>
              <div style={{ marginBottom: 2, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                store: {inf.store_id ?? '—'} · chain: {inf.chain_id ?? '—'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
                {fmtIso(inf.ingested_at)}
              </div>
              {inf.ingested_by && (
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}>
                  by {inf.ingested_by.split('@')[0]}
                </div>
              )}
              {inf.dataset_id && (
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--text-tertiary)',
                    marginBottom: 2,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {datasets.find((d) => d.dataset_id === inf.dataset_id)?.dataset_name ??
                    inf.dataset_id}
                </div>
              )}
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={LABEL_SOURCE_STYLES[inf.label_source]}>
                  {LABEL_SOURCE_LABELS[inf.label_source]}
                </span>
                <button
                  className="eval-btn"
                  style={{ padding: '2px 7px', fontSize: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/gt/audit/${inf.gt_sample_id}`, '_blank');
                  }}
                >
                  Edit
                </button>
              </div>
              <SampleStatusBadges sample={inf} />
            </div>
          </div>
        ))}
        {!loading && samples.length === 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
            No samples found.
          </div>
        )}
      </div>
    </div>
  );
};
